export type PdfTextItem = {
	str: string;
	x: number;
	y: number;
	width: number;
	height: number;
	fontName: string;
	fontSize: number;
	dir: 'ltr' | 'rtl' | string;
	hasEOL: boolean;
};

export type PdfImageMatch = {
	index: number;
	width: number;
	height: number;
	x: number;
	y: number;
	url: string;
	pageNum?: number;
	pageNumber?: number;
};

export const imagePageNum = (image: PdfImageMatch): number =>
	image.pageNumber ?? image.pageNum ?? 1;

export const imageSrc = (url: string): string => {
	if (!url) return '';
	if (
		url.startsWith('data:') ||
		url.startsWith('http://') ||
		url.startsWith('https://') ||
		url.startsWith('blob:')
	) {
		return url;
	}
	return `data:image/png;base64,${url}`;
};

export type PdfExtractPage = {
	pageNum: number;
	width: number;
	height: number;
	rotation: number;
	content: PdfTextItem[];
	fullText: string;
	images?: PdfImageMatch[];
};

export type PdfFormFieldType =
	| 'PDFTextField'
	| 'PDFDropdown'
	| 'PDFCheckBox'
	| 'PDFSignature'
	| string;

export type PdfFormField = {
	fieldName: string;
	fieldType: PdfFormFieldType;
	value: string | string[] | boolean | null;
	options: string[] | null;
	positions?: PdfFieldPosition[];
};

export type PdfFieldPosition = {
	pageNum: number;
	x: number;
	y: number;
	width: number;
	height: number;
	pageWidth?: number;
	pageHeight?: number;
	screenX?: number;
	screenY?: number;
};

type SlotKind = 'text' | 'area' | 'checkbox';

type FormSlot = {
	id: string;
	pageNum: number;
	x: number;
	y: number;
	width: number;
	height: number;
	kind: SlotKind;
	label: string;
	section: string;
};

export type MatchedPdfWidget = {
	id: string;
	field: PdfFormField;
	pageNum: number;
	x: number;
	y: number;
	width: number;
	height: number;
	kind: SlotKind;
	label: string;
};

const RIGHT_MARGIN = 36;
const Y_LINE_TOLERANCE = 4;
const MIN_TEXT_SLOT_WIDTH = 32;
const LABEL_INPUT_GAP = 4;
const CHECKBOX_SIZE = 10;
const CHECKBOX_GAP_MIN = 8;
const CHECKBOX_GAP_MAX = 22;
const AREA_MIN_WORDS = 8;
const AREA_MIN_CHARS = 55;

const KIND_ALIASES: Record<string, string> = {
	telephone: 'phone',
	phone: 'phone',
	'zip code': 'zip',
	zip: 'zip',
	'email address': 'email',
	email: 'email',
	address: 'address',
	city: 'city',
	state: 'state',
	name: 'name',
	firm: 'firm',
	signature: 'signature',
	date: 'date',
	'amount of claim': 'amount',
};

const normalize = (value: string): string =>
	value
		.toLowerCase()
		.replace(/[’]/g, "'")
		.replace(/[:()]/g, ' ')
		.replace(/\s+/g, ' ')
		.replace(/\s+\d+$/, '')
		.trim();

const kindFromLabel = (label: string): string => {
	const n = normalize(label);
	for (const [key, kind] of Object.entries(KIND_ALIASES)) {
		if (n === key || n.endsWith(` ${key}`) || n.startsWith(`${key} `)) {
			return kind;
		}
	}
	if (n.includes('signature')) return 'signature';
	if (n.includes('zip')) return 'zip';
	if (n.includes('phone') || n.includes('telephone')) return 'phone';
	if (n.includes('email')) return 'email';
	if (n.includes('address')) return 'address';
	if (n.includes('city')) return 'city';
	if (n.includes('state')) return 'state';
	if (n.includes('firm')) return 'firm';
	if (n.includes('name')) return 'name';
	if (n.includes('date')) return 'date';
	return n;
};

const sectionFromFieldName = (fieldName: string): string => {
	const parts = normalize(fieldName).split(' ').filter(Boolean);
	if (parts.length === 0) return 'other';
	if (parts[1] === 'representative' || parts[1] === 'rep') {
		return `${parts[0]}-rep`;
	}
	return parts[0];
};

const kindFromFieldName = (fieldName: string): string =>
	kindFromLabel(fieldName);

const isTextLike = (fieldType: PdfFormFieldType): boolean =>
	fieldType === 'PDFTextField' ||
	fieldType === 'PDFDropdown' ||
	fieldType === 'PDFSignature';

const visibleItems = (page: PdfExtractPage): PdfTextItem[] =>
	page.content.filter((item) => item.str.trim().length > 0);

const groupLines = (items: PdfTextItem[]): PdfTextItem[][] => {
	const sorted = [...items].sort((a, b) =>
		Math.abs(a.y - b.y) <= Y_LINE_TOLERANCE ? a.x - b.x : a.y - b.y
	);
	const lines: PdfTextItem[][] = [];
	sorted.forEach((item) => {
		const last = lines[lines.length - 1];
		if (last && Math.abs(last[0].y - item.y) <= Y_LINE_TOLERANCE) {
			last.push(item);
			return;
		}
		lines.push([item]);
	});
	return lines;
};

const wordCount = (label: string): number =>
	label.replace(/:$/, '').trim().split(/\s+/).filter(Boolean).length;

const isJunkCheckboxLabel = (label: string): boolean => {
	const t = label.trim();
	if (t.length <= 1) return true;
	if (/^[^\w]+$/.test(t)) return true;
	if (/^https?:\/\//i.test(t)) return true;
	if (/^[a-z]/.test(t) && !/^(yes|no|n\/a|other)\b/i.test(t)) return true;
	return false;
};

const isAreaPrompt = (label: string): boolean => {
	const t = label.trim();
	if (!/:(\s*\$)?$/.test(t) && !t.endsWith(':')) return false;
	return wordCount(t) >= AREA_MIN_WORDS || t.length >= AREA_MIN_CHARS;
};

const isFillInLabel = (label: string): boolean => {
	const t = label.trim();
	if (isAreaPrompt(t)) return true;
	if (t.length > 90) return false;
	return /:(\s*\$)?$/.test(t);
};

const isProseLine = (line: PdfTextItem[]): boolean => {
	const text = line
		.map((item) => item.str)
		.join(' ')
		.replace(/\s+/g, ' ')
		.trim();
	if (line.some((item) => isFillInLabel(item.str))) return false;
	const words = wordCount(text);
	if (words >= 8) return true;
	if (/[.?!]$/.test(text) && words >= 5) return true;
	return false;
};

const isWordFragmentGap = (prev: PdfTextItem, item: PdfTextItem): boolean => {
	const prevText = prev.str.trimEnd();
	const nextText = item.str.trimStart();
	if (!prevText || !nextText) return true;
	const prevEndsAlpha = /[A-Za-z0-9]$/.test(prevText);
	const nextStartsLower = /^[a-z0-9]/.test(nextText);
	if (prevEndsAlpha && nextStartsLower) return true;
	return (
		prevText.length <= 2 &&
		/^[A-Za-z]/.test(nextText) &&
		!/[.:]$/.test(prevText)
	);
};

const gapBefore = (item: PdfTextItem, prev: PdfTextItem): number =>
	item.x - (prev.x + prev.width);

const isCheckboxGap = (gap: number): boolean =>
	gap >= CHECKBOX_GAP_MIN && gap <= CHECKBOX_GAP_MAX;

const checkboxInGap = (
	item: PdfTextItem,
	prev: PdfTextItem
): { x: number; y: number; width: number; height: number } => {
	const gapStart = prev.x + prev.width;
	const gap = gapBefore(item, prev);
	const size = Math.min(CHECKBOX_SIZE, Math.max(9, gap - 2));
	const labelHeight = item.height || item.fontSize || size;
	return {
		x: gapStart + (gap - size) / 2,
		y: item.y + (labelHeight - size) / 2,
		width: size,
		height: size,
	};
};

const updateSection = (line: PdfTextItem[], current: string): string => {
	const first = line[0];
	if (!first || first.x >= 80) return current;
	const label = first.str.trim();
	if (label.includes(':')) return current;
	if (wordCount(label) > 3 || label.length > 40) return current;
	const section = normalize(label);
	if (/representative/i.test(label) && current && current !== 'other') {
		return `${current}-rep`;
	}
	return section || current;
};

const buildSlots = (pages: PdfExtractPage[]): FormSlot[] => {
	const slots: FormSlot[] = [];
	let section = 'other';

	pages.forEach((page) => {
		const lines = groupLines(visibleItems(page));
		const rightEdge = page.width - RIGHT_MARGIN;

		lines.forEach((line, lineIndex) => {
			section = updateSection(line, section);
			const checkboxItemIndexes = new Set<number>();
			const skipCheckboxes = isProseLine(line);

			if (!skipCheckboxes) {
				line.forEach((item, itemIndex) => {
					if (itemIndex === 0) return;
					const prev = line[itemIndex - 1];
					if (
						!isCheckboxGap(gapBefore(item, prev)) ||
						isJunkCheckboxLabel(item.str) ||
						isWordFragmentGap(prev, item)
					) {
						return;
					}
					const box = checkboxInGap(item, prev);
					checkboxItemIndexes.add(itemIndex);
					slots.push({
						id: `${page.pageNum}-cb-${lineIndex}-${itemIndex}`,
						pageNum: page.pageNum,
						x: box.x,
						y: box.y,
						width: box.width,
						height: box.height,
						kind: 'checkbox',
						label: item.str.trim(),
						section,
					});
				});
			}

			line.forEach((item, itemIndex) => {
				if (!isFillInLabel(item.str)) return;

				const next = line
					.slice(itemIndex + 1)
					.find((candidate) => candidate.str.trim().length > 0);
				const nextIndex = next ? line.indexOf(next) : -1;
				if (
					nextIndex >= 0 &&
					checkboxItemIndexes.has(nextIndex) &&
					!checkboxItemIndexes.has(itemIndex)
				) {
					return;
				}

				const area = isAreaPrompt(item.str);
				const startX = item.x + item.width + LABEL_INPUT_GAP;
				const endX = next ? next.x - LABEL_INPUT_GAP : rightEdge;
				const width = endX - startX;
				const height = area
					? Math.min(72, Math.max(28, item.fontSize * 4))
					: Math.max(12, item.height || item.fontSize + 3);

				if (!area && width < MIN_TEXT_SLOT_WIDTH) return;

				slots.push({
					id: `${page.pageNum}-tx-${lineIndex}-${itemIndex}`,
					pageNum: page.pageNum,
					x: area ? Math.min(item.x, RIGHT_MARGIN + 4) : startX,
					y: area ? item.y + (item.height || item.fontSize) + 4 : item.y - 1,
					width: area ? rightEdge - item.x : width,
					height,
					kind: area ? 'area' : 'text',
					label: item.str.trim(),
					section,
				});
			});
		});
	});

	return slots;
};

const scoreMatch = (field: PdfFormField, slot: FormSlot): number => {
	if (field.fieldType === 'PDFCheckBox' && slot.kind !== 'checkbox') return 0;
	if (field.fieldType !== 'PDFCheckBox' && slot.kind === 'checkbox') return 0;

	const fieldSection = sectionFromFieldName(field.fieldName);
	const fieldKind = kindFromFieldName(field.fieldName);
	const slotKind = kindFromLabel(slot.label);
	let score = 0;

	if (fieldSection === slot.section) score += 40;
	if (fieldKind && slotKind && fieldKind === slotKind) score += 50;
	if (normalize(field.fieldName).includes(normalize(slot.label))) score += 20;
	if (normalize(slot.label).includes(fieldKind)) score += 10;
	if (field.fieldType === 'PDFSignature' && slotKind === 'signature')
		score += 40;
	if (field.fieldType === 'PDFCheckBox' && slot.kind === 'checkbox') {
		if (normalize(field.fieldName).includes(normalize(slot.label))) score += 30;
	}

	return score;
};

const widgetId = (fieldIndex: number, fieldName: string): string =>
	`${fieldIndex}:${fieldName}`;

const kindFromField = (field: PdfFormField, height: number): SlotKind => {
	if (field.fieldType === 'PDFCheckBox') return 'checkbox';
	if (height > 28) return 'area';
	return 'text';
};

type CssRect = {
	pageNum: number;
	x: number;
	y: number;
	width: number;
	height: number;
};

const toCssPosition = (
	raw: PdfFieldPosition,
	pages: PdfExtractPage[]
): CssRect => {
	const page = pages.find((candidate) => candidate.pageNum === raw.pageNum);
	const pageWidth = raw.pageWidth || page?.width || 1;
	const pageHeight = raw.pageHeight || page?.height || 1;
	const sx = page ? page.width / pageWidth : 1;
	const sy = page ? page.height / pageHeight : 1;
	const x = (raw.screenX ?? raw.x) * sx;
	const y =
		raw.screenY != null
			? raw.screenY * sy
			: raw.pageHeight != null
			? (raw.pageHeight - raw.y - raw.height) * sy
			: raw.y * sy;
	return {
		pageNum: raw.pageNum,
		x,
		y,
		width: raw.width * sx,
		height: raw.height * sy,
	};
};

const captionForPosition = (
	pages: PdfExtractPage[],
	pos: CssRect,
	checkbox: boolean
): string => {
	const page = pages.find((candidate) => candidate.pageNum === pos.pageNum);
	if (!page) return '';
	const items = visibleItems(page);
	const sameBand = items.filter((item) => Math.abs(item.y - pos.y) <= 18);

	if (checkbox) {
		const right = sameBand
			.filter((item) => item.x >= pos.x - 4)
			.sort((a, b) => a.x - b.x);
		return right.find((item) => item.str.trim().length > 1)?.str.trim() || '';
	}

	const left = sameBand
		.filter((item) => item.x + item.width <= pos.x + 12)
		.sort((a, b) => pos.x - (a.x + a.width) - (pos.x - (b.x + b.width)));
	const leftLabel = left.find((item) => isFillInLabel(item.str));
	if (leftLabel) return leftLabel.str.trim();

	const above = items
		.filter(
			(item) =>
				item.y < pos.y - 1 && pos.y - item.y <= 32 && item.x < pos.x + pos.width
		)
		.sort((a, b) => b.y - a.y);
	const aboveLabel = above.find((item) => isFillInLabel(item.str));
	if (aboveLabel) return aboveLabel.str.trim();
	return left[0]?.str.trim() || '';
};

const slotDistance = (
	slot: FormSlot,
	pos: CssRect
): { dist: number; overlap: number } => {
	const dx = slot.x + slot.width / 2 - (pos.x + pos.width / 2);
	const dy = slot.y + slot.height / 2 - (pos.y + pos.height / 2);
	const overlapX = Math.max(
		0,
		Math.min(slot.x + slot.width, pos.x + pos.width) - Math.max(slot.x, pos.x)
	);
	const overlapY = Math.max(
		0,
		Math.min(slot.y + slot.height, pos.y + pos.height) - Math.max(slot.y, pos.y)
	);
	return { dist: Math.hypot(dx, dy), overlap: overlapX * overlapY };
};

const nearestSlot = (
	slots: FormSlot[],
	pos: CssRect,
	field: PdfFormField,
	usedSlots: Set<string>
): FormSlot | undefined => {
	const candidates = slots.filter((slot) => {
		if (usedSlots.has(slot.id)) return false;
		if (slot.pageNum !== pos.pageNum) return false;
		if (field.fieldType === 'PDFCheckBox' && slot.kind !== 'checkbox') {
			return false;
		}
		if (field.fieldType !== 'PDFCheckBox' && slot.kind === 'checkbox') {
			return false;
		}
		return true;
	});
	if (candidates.length === 0) return undefined;

	const ranked = candidates
		.map((slot) => ({ slot, ...slotDistance(slot, pos) }))
		.sort((a, b) => b.overlap - a.overlap || a.dist - b.dist);

	const best = ranked[0];
	if (!best) return undefined;
	if (best.overlap > 0 || best.dist < 96) return best.slot;
	return undefined;
};

const widgetFromSlot = (
	field: PdfFormField,
	fieldIndex: number,
	slot: FormSlot
): MatchedPdfWidget => ({
	id: widgetId(fieldIndex, field.fieldName),
	field,
	pageNum: slot.pageNum,
	x: slot.x,
	y: slot.y,
	width: slot.width,
	height: slot.height,
	kind: slot.kind,
	label: slot.label,
});

export const matchPdfFormFields = (
	pages: PdfExtractPage[],
	fields: PdfFormField[],
	positions: PdfFieldPosition[] = []
): { widgets: MatchedPdfWidget[]; unmatched: PdfFormField[] } => {
	const slots = buildSlots(pages);
	const usedSlots = new Set<string>();
	const usedFieldIndexes = new Set<number>();
	const widgets: MatchedPdfWidget[] = [];

	fields.forEach((field, fieldIndex) => {
		const raw = field.positions?.[0] ?? positions[fieldIndex];
		if (!raw) return;
		const pos = toCssPosition(raw, pages);
		const checkbox = field.fieldType === 'PDFCheckBox';
		const caption = captionForPosition(pages, pos, checkbox);
		const slot = nearestSlot(slots, pos, field, usedSlots);
		if (slot) usedSlots.add(slot.id);
		usedFieldIndexes.add(fieldIndex);
		widgets.push({
			id: widgetId(fieldIndex, field.fieldName),
			field,
			pageNum: pos.pageNum,
			x: pos.x,
			y: pos.y,
			width: pos.width,
			height: pos.height,
			kind: checkbox
				? 'checkbox'
				: slot?.kind ?? kindFromField(field, pos.height),
			label: caption || slot?.label || field.fieldName,
		});
	});

	const leftoverFields = fields
		.map((field, fieldIndex) => ({ field, fieldIndex }))
		.filter(({ fieldIndex }) => !usedFieldIndexes.has(fieldIndex));

	const pairs = leftoverFields.flatMap(({ field, fieldIndex }) =>
		slots.map((slot) => ({
			field,
			fieldIndex,
			slot,
			score: scoreMatch(field, slot),
		}))
	);

	pairs
		.filter((pair) => pair.score >= 50)
		.sort((a, b) => b.score - a.score)
		.forEach((pair) => {
			if (usedFieldIndexes.has(pair.fieldIndex)) return;
			if (usedSlots.has(pair.slot.id)) return;
			usedFieldIndexes.add(pair.fieldIndex);
			usedSlots.add(pair.slot.id);
			widgets.push(widgetFromSlot(pair.field, pair.fieldIndex, pair.slot));
		});

	const leftoverSlots = slots.filter((slot) => !usedSlots.has(slot.id));

	const takeNext = (
		kind: SlotKind,
		field: PdfFormField,
		fieldIndex: number
	): boolean => {
		const index = leftoverSlots.findIndex((slot) => slot.kind === kind);
		if (index < 0) return false;
		const [slot] = leftoverSlots.splice(index, 1);
		usedFieldIndexes.add(fieldIndex);
		widgets.push(widgetFromSlot(field, fieldIndex, slot));
		return true;
	};

	leftoverFields.forEach(({ field, fieldIndex }) => {
		if (usedFieldIndexes.has(fieldIndex)) return;
		if (field.fieldType === 'PDFCheckBox') {
			takeNext('checkbox', field, fieldIndex);
			return;
		}
		if (field.fieldType === 'PDFSignature') {
			takeNext('text', field, fieldIndex) ||
				takeNext('area', field, fieldIndex);
			return;
		}
		if (isTextLike(field.fieldType)) {
			takeNext('text', field, fieldIndex) ||
				takeNext('area', field, fieldIndex);
		}
	});

	return {
		widgets,
		unmatched: leftoverFields
			.filter(({ fieldIndex }) => !usedFieldIndexes.has(fieldIndex))
			.map(({ field }) => field),
	};
};

export const initialFieldValue = (field: PdfFormField): string | boolean => {
	if (field.fieldType === 'PDFCheckBox') return Boolean(field.value);
	if (Array.isArray(field.value)) return field.value[0] ?? '';
	return field.value ?? '';
};
